import { Component, effect, inject, Input, ViewChild } from '@angular/core';
import { WidgetStore } from '../../../../../stores/grid/widget.state';
import {
  CustomGridStackWidget,
  GridWidgetConfigService,
  TeaserItem,
} from '../../../../../services/grid-widget-config.service';
import { getState } from '@ngrx/signals';
import { BookmarkStore } from '../../../../../stores/bookmark/bookmark.state';
import { Bookmark } from '../../../../../models/bookmark';
import { Carousel, CarouselModule } from 'primeng/carousel';
import { PrimeTemplate } from 'primeng/api';
import { ButtonDirective } from 'primeng/button';
import { BookmarkButtonComponent } from '../../../../atoms/bookmark-button/bookmark-button.component';
import {
  getUploadPath,
  removeHtmlFromString,
} from '../../../../../utility/app.utility';
import { ImageStore } from '../../../../../stores/image/image.state';
import { GlobalsStore } from '../../../../../stores/globals.state';
import { StoryStore } from '../../../../../stores/story/story.state';
import { StoryButtonComponent } from '../../../../atoms/story-button/story-button.component';
import { TopicStore } from '../../../../../stores/topic/topic.state';
import { TopicButtonComponent } from '../../../../atoms/topic-button/topic-button.component';

@Component({
  selector: 'app-teaser',
  standalone: true,
  imports: [
    CarouselModule,
    PrimeTemplate,
    ButtonDirective,
    BookmarkButtonComponent,
    StoryButtonComponent,
    TopicButtonComponent,
  ],
  templateUrl: './teaser.component.html',
  styleUrl: './teaser.component.scss',
})
export class TeaserComponent {
  readonly bookmarkStore = inject(BookmarkStore);
  readonly storyStore = inject(StoryStore);
  readonly topicStore = inject(TopicStore);
  readonly widgetStore = inject(WidgetStore);
  readonly imageStore = inject(ImageStore);
  readonly globalsStore = inject(GlobalsStore);

  link = '';
  bookmark: Bookmark | null = null;
  teaserItems: TeaserItem[] = [];
  autoplayIntervall = 0;
  protected config: CustomGridStackWidget | null = null;
  @ViewChild('carousel') carousel!: Carousel;
  @Input() id!: string;

  constructor(private configService: GridWidgetConfigService) {
    effect(() => {
      const bookmarkState = getState(this.bookmarkStore);
      const widgetState = getState(this.widgetStore);

      if (!this.id) return;

      this.config = this.widgetStore.get(this.id);
      this.teaserItems = this.config?.widget?.teaser?.items || [];

      this.restartAutoplay();
    });
  }

  /**
   * Retrieves the source or copyright information for the given teaser's background.
   *
   * @param {TeaserItem} teaser - The teaser item containing the background information.
   * @return {string} The source or copyright string based on the teaser's background, or an empty string if none is found.
   */
  protected getSource(teaser: TeaserItem): string {
    if (teaser.backgroundId) {
      const image = this.imageStore.getById(teaser.backgroundId);
      return image?.source || '';
    } else if (teaser.backgroundSrc) {
      const image = this.configService.getPoolImageBySrc(teaser.backgroundSrc);
      return image?.copyright || '';
    }
    return '';
  }

  /**
   * Retrieves the source URL of an image associated with a given teaser item.
   * It checks for the presence of a background image ID and fetches the respective image
   * source from the store, constructing the path based on the active commune. If no
   * background ID is found, it falls back to the teaser's background source value.
   *
   * @param {TeaserItem} teaser - The teaser item containing information about the background image.
   * @return {string} The source URL of the image or an empty string if no image information is available.
   */
  getImageSrc(teaser: TeaserItem): string {
    const communeSegment = this.globalsStore.activeCommune()?._id || '';
    if (teaser.backgroundId) {
      const image = this.imageStore.getById(teaser.backgroundId);
      return image
        ? getUploadPath(`imagePool/${communeSegment}`, image.src, true)
        : '';
    }
    return teaser.backgroundSrc || '';
  }

  /**
   * Checks whether the autoplay feature is enabled in the configuration.
   *
   * @return {boolean} Returns true if autoplay is enabled; otherwise, false.
   */
  get hasAutoplay(): boolean {
    return !!this.config?.widget?.teaser?.autoPlay;
  }

  /**
   * Restarts the autoplay functionality of the carousel. Stops the current autoplay and, if enabled, schedules a restart with the predefined interval.
   *
   * @return {void} Does not return a value.
   */
  // NG21-MIGRATION(stufe-3): nutzt interne Carousel-Methoden (stop/startAutoplay, isPlaying) -> gegen v19–21-API prüfen (§9.3)
  restartAutoplay(): void {
    if (this.carousel) {
      this.autoplayIntervall = 0;
      this.carousel.stopAutoplay();

      // Start autoplay if `hasAutoplay` is greater than 0
      if (this.hasAutoplay && !this.carousel.isPlaying()) {
        this.autoplayIntervall = 5000;
        setTimeout(() => {
          this.carousel.startAutoplay();
        }, 10);
      }
    }
  }

  protected readonly removeHtmlFromString = removeHtmlFromString;
}
